import { useEffect, useImperativeHandle, useRef, useState, type Ref } from 'react';
import { cn } from '@/components/ui';
import { KanameMark } from './KanameMark';
import { BLOOM_SLOW, DEPTH_FADE_MS, bloomEnd } from './schedule';

/*
  KanameIntro — знакомство на странице входа (docs/brand-concept.md §3–4, §9). Тот же лёгкий знак,
  что в меню, только неспешно. Каждый вход — полное знакомство в одном темпе:

  - first — видно одно дышащее ядро; по касанию ядра (или само через 2,6 с) веер раскрывается
            с 6 часов по часовой, последние лепестки садятся всё медленнее; собранный веер стоит,
            затем лепестки медленно темнеют в окна «глубины», за ними идёт листопад;
  - still — меньше движения в системе: знак собран, окна без листопада.

  Фазы: core → bloom → depth (окна темнеют) → settled (знакомство закончено). Пока знакомство идёт,
  ядро стоит на месте и не отвлекает от раскрытия; когда закончилось — чуть тянется к курсору:
  видно, что перед тобой живой элемент, а не картинка.
*/

export type IntroMode = 'first' | 'still';

export interface KanameIntroHandle {
  /** Раскрыть веер сейчас (касание ядра). */
  start: () => void;
  /** Проиграть знакомство заново. */
  replay: () => void;
  /** Лепестки уже окна глубины (или темнеют в них). */
  isDepth: () => boolean;
}

export type IntroPhase = 'core' | 'bloom' | 'depth' | 'settled';
type Phase = IntroPhase;

const AUTO_START_MS = 2600;
/** Когда собранный веер начинает темнеть: последний лепесток сел, и веер постоял. */
const BLOOM_MS = bloomEnd(BLOOM_SLOW) + 1400;
/** Насколько ядро тянется к курсору, в единицах знака. */
const REACH = 12;

interface KanameIntroProps {
  mode: IntroMode;
  ref?: Ref<KanameIntroHandle>;
  /** Сцена узнаёт, что началось раскрытие и открылась глубина: под это проявляются слово и слоган. */
  onPhaseChange?: (phase: IntroPhase) => void;
  className?: string;
}

export function KanameIntro({ mode, ref, onPhaseChange, className }: KanameIntroProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<Phase>(mode === 'still' ? 'settled' : 'core');
  const [shift, setShift] = useState({ x: 0, y: 0 });
  const deep = phase === 'depth' || phase === 'settled';
  // Ядро тянется к курсору, только когда знакомство закончилось.
  const follow = phase === 'settled' && mode !== 'still';

  const start = () => setPhase((p) => (p === 'core' ? 'bloom' : p));

  useImperativeHandle(ref, () => ({
    start,
    replay: () => {
      setShift({ x: 0, y: 0 });
      setPhase('core');
      window.setTimeout(() => setPhase('bloom'), 500);
    },
    isDepth: () => deep,
  }));

  useEffect(() => {
    onPhaseChange?.(phase);
  }, [phase, onPhaseChange]);

  // Ядро ждёт касания; не дождалось — веер раскрывается сам.
  useEffect(() => {
    if (mode === 'still' || phase !== 'core') return;
    const t = window.setTimeout(() => setPhase('bloom'), AUTO_START_MS);
    return () => window.clearTimeout(t);
  }, [mode, phase]);

  // Веер собрался и постоял — лепестки темнеют в окна глубины.
  useEffect(() => {
    if (phase !== 'bloom') return;
    const t = window.setTimeout(() => setPhase('depth'), BLOOM_MS);
    return () => window.clearTimeout(t);
  }, [phase]);

  // Окна проявились целиком — знакомство закончено.
  useEffect(() => {
    if (phase !== 'depth') return;
    const t = window.setTimeout(() => setPhase('settled'), DEPTH_FADE_MS);
    return () => window.clearTimeout(t);
  }, [phase]);

  // Ядро тянется к курсору: вектор от центра знака, не дальше REACH.
  useEffect(() => {
    if (!follow) return;
    let frame = 0;
    const onMove = (e: PointerEvent) => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const host = hostRef.current;
        if (!host) return;
        const r = host.getBoundingClientRect();
        const dx = (e.clientX - (r.left + r.width / 2)) / r.width;
        const dy = (e.clientY - (r.top + r.height / 2)) / r.height;
        const len = Math.hypot(dx, dy) || 1;
        const k = Math.min(1, len * 2) * REACH;
        setShift({ x: (dx / len) * k, y: (dy / len) * k });
      });
    };
    window.addEventListener('pointermove', onMove);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('pointermove', onMove);
    };
  }, [follow]);

  return (
    <div
      ref={hostRef}
      role="img"
      aria-label="Знак Kaname: шесть лепестков вокруг кораллового ядра"
      onClick={(e) => {
        // Касание ядра (центр знака) раскрывает веер.
        const r = e.currentTarget.getBoundingClientRect();
        const d = Math.hypot(e.clientX - (r.left + r.width / 2), e.clientY - (r.top + r.height / 2));
        if (d < r.width * 0.1) start();
      }}
      className={cn('aspect-square', phase === 'core' && 'cursor-pointer', className)}
    >
      <KanameMark
        open={phase !== 'core'}
        tempo="slow"
        variant={deep ? 'window' : 'solid'}
        leaves={deep && mode !== 'still'}
        breathing={mode !== 'still'}
        coreShift={follow ? shift : undefined}
        className="size-full"
      />
    </div>
  );
}
